import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ProgressRing } from "@/components/ui/progress-ring";
import {
  beginLoading,
  subscribeLoading,
} from "@/lib/loading-activity";
import { cn } from "@/lib/utils";

/**
 * App-wide Progress Ring:
 * - brief pulse on every client-side route change
 * - stays visible while any instrumented API request is in flight
 */
export function GlobalLoadingIndicator() {
  const location = useLocation();
  const [inflight, setInflight] = useState(0);
  const firstRender = useRef(true);

  useEffect(() => subscribeLoading(setInflight), []);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const end = beginLoading();
    const timer = window.setTimeout(() => {
      end();
    }, 280);
    return () => {
      window.clearTimeout(timer);
      end();
    };
  }, [location.pathname, location.search]);

  const active = inflight > 0;

  return (
    <div
      className={cn(
        "global-loading-indicator pointer-events-none fixed right-4 top-4 z-toast flex items-center gap-2 rounded-full border border-border-standard bg-surface-container-lowest/95 px-2.5 py-1.5 shadow-floating backdrop-blur-sm transition-[opacity,transform] duration-standard ease-standard",
        active
          ? "translate-y-0 opacity-100"
          : "pointer-events-none -translate-y-1 opacity-0",
      )}
      aria-hidden={!active}
      data-testid="global-loading-indicator"
      data-active={active ? "true" : "false"}
    >
      {/* Keep ring mounted while visible so CSS animation never restarts mid-spin */}
      {active ? <ProgressRing size="sm" label="Loading" /> : null}
      <span className="text-xs font-medium text-text-muted">Loading</span>
    </div>
  );
}
